import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchGroupBookings,
  fetchGroupBooking,
  createGroupBooking,
  updateGroupBooking,
  confirmGroupBooking,
  cancelGroupBooking,
  type GroupBookingsQuery,
  type CreateGroupBookingPayload,
  type UpdateGroupBookingPayload,
} from '@/services/api/groups.api';

export function useGroupBookings(query?: GroupBookingsQuery) {
  return useQuery({
    queryKey: ['group-bookings', query],
    queryFn: () => fetchGroupBookings(query ?? {}),
    enabled: !!query?.propertyId,
    staleTime: 30_000,
  });
}

export function useGroupBooking(id: string | null) {
  return useQuery({
    queryKey: ['group-bookings', id],
    queryFn: () => fetchGroupBooking(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

export function useCreateGroupBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGroupBookingPayload) => createGroupBooking(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useUpdateGroupBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateGroupBookingPayload }) =>
      updateGroupBooking(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useConfirmGroupBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => confirmGroupBooking(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

export function useCancelGroupBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelGroupBooking(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}
